import { supabase } from '../../supabase/client';
import { ShiftInsert, ShiftUpdate, Shift } from '../../../types/schedule';
import { PostgrestError } from '@supabase/supabase-js';

// Check if supabase client is available
if (!supabase) {
  throw new Error('Supabase client not initialized');
}

export const shiftService = {
  /**
   * Create a new shift
   */
  async createShift(shift: ShiftInsert): Promise<{ data: Shift | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('shifts')
      .insert(shift)
      .select('*')
      .single();
    
    return { data, error };
  },

  /**
   * Create multiple shifts at once
   */
  async createShifts(shifts: ShiftInsert[]): Promise<{ data: Shift[] | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('shifts')
      .insert(shifts)
      .select('*');
    
    return { data, error };
  },

  /**
   * Get all shifts for a schedule
   */
  async getShiftsByScheduleId(scheduleId: string): Promise<{ data: Shift[] | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    // Use a join or a separate query to get user names
    const { data, error } = await supabase
      .from('shifts')
      .select(`
        *,
        provider:provider_id (
          id,
          full_name
        )
      `)
      .eq('schedule_id', scheduleId)
      .order('date', { ascending: true });
    
    // Transform the data to include provider_name
    const transformedData = data?.map(shift => {
      return {
        ...shift,
        provider_name: shift.provider?.full_name || null
      };
    });
    
    return { data: transformedData || null, error };
  },

  /**
   * Update a shift
   */
  async updateShift(id: string, updates: ShiftUpdate): Promise<{ data: Shift | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('shifts')
      .update(updates)
      .eq('id', id)
      .select('*')
      .single();
    
    return { data, error };
  },

  /**
   * Delete a shift
   */
  async deleteShift(id: string): Promise<{ error: PostgrestError | null }> {
    if (!supabase) {
      return { error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { error } = await supabase
      .from('shifts')
      .delete()
      .eq('id', id);
    
    return { error };
  },

  /**
   * Get shifts for a specific provider
   */
  async getProviderShifts(
    providerId: string, 
    scheduleId: string
  ): Promise<{ data: Shift[] | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('shifts')
      .select('*')
      .eq('schedule_id', scheduleId)
      .eq('provider_id', providerId)
      .order('date', { ascending: true });
    
    return { data, error };
  },

  /**
   * Assign provider to shift
   */
  async assignProviderToShift(
    shiftId: string, 
    providerId: string | null
  ): Promise<{ data: Shift | null; error: PostgrestError | null }> {
    return this.updateShift(shiftId, { provider_id: providerId });
  }
}; 